import React from 'react';
import PropTypes from 'prop-types';
import { Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { trackEvent } from 'user-analytics';
import { ACCOMMODATION_PROMO_BANNER_CLICKED } from 'constants/TrackEvents';
import accommodationPromoPlaceholder from 'images/accommodation-promo-placeholder.png';
import PurchaseCompleteBannerLayout from './PurchaseCompleteBannerLayout';

/**
 * AccommodationPromoBanner component.
 * Render the accommodation promo banner (visibility is controlled by PurchaseCompleteBanners).
 * @param {object} props - purchaseType, isOpenTicket, className.
 * @param {string} props.purchaseType - Purchase type (one_way/round_trip).
 * @param {boolean} props.isOpenTicket - Whether the purchase has open tickets.
 * @param {string} props.className - Additional CSS classes for the banner container.
 * @param {string} props.imageColumnSize - Image column width: compact or expanded.
 * @returns {JSX.Element} The rendered banner.
 */
function AccommodationPromoBanner({ purchaseType, isOpenTicket, className, imageColumnSize }) {
  const { t } = useTranslation('purchase');
  const { accommodationWhatsappUrl: whatsappUrl, brand } = useWhitelabelEnvs();

  /**
   * Handle accommodation promo WhatsApp link click analytics.
   * @returns {void}
   */
  const handleClick = () => {
    trackEvent(ACCOMMODATION_PROMO_BANNER_CLICKED, {
      'Page': 'Confirmation',
      'Placement': 'purchase_complete_banner',
      'Brand': brand,
      'Purchase Type': purchaseType,
      'Is Open Ticket': isOpenTicket,
      'URL': whatsappUrl,
    });
  };

  return (
    <PurchaseCompleteBannerLayout
      className={className}
      imageSrc={accommodationPromoPlaceholder}
      imageAlt={t('label.accommodation_promo_title')}
      bannerHref={whatsappUrl}
      onBannerClick={handleClick}
      imageColumnSize={imageColumnSize}
    >
      <Text color="primary" weight="semibold">
        {t('label.accommodation_promo_title')}
      </Text>
      <Text size="S" color="info" underline>
        {t('label.accommodation_whatsapp_link')}
      </Text>
    </PurchaseCompleteBannerLayout>
  );
}

AccommodationPromoBanner.propTypes = {
  purchaseType: PropTypes.oneOf(['one_way', 'round_trip']),
  isOpenTicket: PropTypes.bool,
  className: PropTypes.string,
  imageColumnSize: PropTypes.oneOf(['compact', 'expanded']),
};

AccommodationPromoBanner.defaultProps = {
  purchaseType: 'one_way',
  isOpenTicket: false,
  className: '',
  imageColumnSize: 'compact',
};

export default AccommodationPromoBanner;
